import { Button, Icon, MiniLoader } from '@pwc/pwc-deals-ui';
import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get as _get } from 'lodash';
import { getWorkspaceId } from 'features/project/projectSlice';
import './TableauReport.scss';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  convertToSingleTabEmbedUrl,
  getTabNameForEmbedUrl,
  openFullScreen,
  resizeTableau,
  TABLEAU_SHARING_SUCCESS_STATUS,
} from 'common/components/SelectReport/tableauUtils';
import SyncedInfo from 'common/components/Autosync/SyncedInfo.tsx';
import TableauErrorMessage from 'common/components/SelectReport/TableauErrorMessage/TableauErrorMessage';
import AsyncSharingStatusMessage, {
  LibraryItemTypeEnum,
} from 'common/components/AsyncSharingStatusMessage/AsyncSharingStatusMessage.tsx';
import {
  getTableauReportDetail,
  getTableauAutoSyncUrl,
} from '../tableauService.ts';
import {
  getIsThirdPartyMember,
  getUserRole,
} from '../../../common/redux/security/loggedInUser/loggedInUserSlice';

const TableauReport = ({
  item,
  projectId,
  handlePageSelect,
  handleRenderStatus,
  height,
  hasContentError = false,
  showLoadingMsg = true,
}) => {
  const { t } = useTranslation();
  const workspaceId = useSelector(getWorkspaceId);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const [embedUrl, setEmbedUrl] = useState(undefined);

  const tableauContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApiError, setIsApiError] = useState(false);
  const vizRef = useRef();
  const autoSyncUrlRef = useRef('');
  const dimensionsRef = useRef({
    height: height || 650,
    width: '100%',
  });
  const mountedRef = useRef(true);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  let ClientMessage = null;

  if (item.status && item.status !== TABLEAU_SHARING_SUCCESS_STATUS) {
    ClientMessage = (
      <AsyncSharingStatusMessage
        status={item.status}
        type={LibraryItemTypeEnum.Tableau}
      />
    );
  }

  useEffect(() => {
    if (!item.data) {
      item.data = item.content;
    }
    if (!item.reportId) {
      item.reportId = _get(
        JSON.parse(item.data ?? '{}'),
        'ReportId',
        undefined
      );
    }

    const loadAutoSyncUrl = async () => {
      try {
        const data = await getTableauAutoSyncUrl(
          projectId,
          workspaceId,
          item.reportId
        );
        autoSyncUrlRef.current = data.embedUrl;
      } catch (e) {
        setLoading(false);
        setError(true);
      }
    };
    if (
      projectId &&
      workspaceId &&
      item &&
      item.reportId &&
      !item.isShared &&
      !ClientMessage
    ) {
      loadAutoSyncUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, projectId, workspaceId]);

  useEffect(() => {
    const fetchEmbedUrl = async () => {
      try {
        const data = await getTableauReportDetail(
          projectId,
          workspaceId,
          item.reportId
        );

        if (!mountedRef.current) {
          return;
        }

        if (data === false) {
          setIsApiError(true);
          return;
        }

        if (data && data.embedUrl) {
          setEmbedUrl(data.embedUrl);
        }
      } catch (e) {
        setLoading(false);
        setError(true);
      }
    };

    if (workspaceId && projectId && item.reportId) {
      fetchEmbedUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, item, workspaceId, item.tabName]);

  useEffect(() => {
    if (embedUrl && item.tabName) {
      const tabNameForEmbedUrl = getTabNameForEmbedUrl(item.tabName);
      const url = convertToSingleTabEmbedUrl(embedUrl, tabNameForEmbedUrl);
      setEmbedUrl(url);
    }
  }, [embedUrl, item.tabName]);

  useEffect(() => {
    async function embedTableau() {
      if (error) {
        setLoading(false);
        return;
      }

      if (!mountedRef.current) {
        return;
      }

      if (!item || !item.data) {
        return;
      }
      const options = {
        hideTabs: !!item.tabName,
        hideToolbar: true,
        width: dimensionsRef.current.width,
        height: dimensionsRef.current.height,
        onFirstVizSizeKnown: () => {
          setLoading(false);
        },
        onFirstInteractive: () => {
          setLoading(false);
          if (tableauContainerRef.current) {
            tableauContainerRef.current.style.overflow = 'hidden';
            resizeTableau(vizRef, tableauContainerRef, dimensionsRef);
          }
        },
      };
      if (embedUrl) {
        let url = embedUrl;

        if (item.tabName) {
          const tabNameForEmbedUrl = getTabNameForEmbedUrl(item.tabName);
          url = convertToSingleTabEmbedUrl(url, tabNameForEmbedUrl);
        }

        if (!vizRef.current) {
          const instance = new window.tableau.Viz(
            tableauContainerRef.current,
            url,
            options
          );
          vizRef.current = instance;
        }
      }
    }
    if (embedUrl && tableauContainerRef.current) {
      embedTableau();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handlePageSelect,
    handleRenderStatus,
    height,
    error,
    embedUrl,
    item.tabName,
  ]);

  const setFullScreenHeight = () => {
    if (
      document.webkitIsFullScreen &&
      document.mozFullScreen &&
      document.msFullscreenElement
    ) {
      tableauContainerRef.current.style.height = '100vh';
    } else {
      tableauContainerRef.current.style.height = `${height || 650}px`;
    }
  };

  const setEventListeners = useCallback(
    (active) => {
      if (active) {
        window.addEventListener(
          'resize',
          resizeTableau.bind(
            this,
            vizRef,
            tableauContainerRef,
            dimensionsRef,
            null
          )
        );
        document.addEventListener(
          'fullscreenchange',
          setFullScreenHeight,
          false
        );
        document.addEventListener(
          'mozfullscreenchange',
          setFullScreenHeight,
          false
        );
        document.addEventListener(
          'MSFullscreenChange',
          setFullScreenHeight,
          false
        );
        document.addEventListener(
          'webkitfullscreenchange',
          setFullScreenHeight,
          false
        );
        if (vizRef.current) {
          vizRef.current.addEventListener(
            'tabswitch',
            resizeTableau.bind(
              this,
              vizRef,
              tableauContainerRef,
              dimensionsRef,
              null
            )
          );
        }
      } else {
        window.removeEventListener('resize', resizeTableau);
        if (vizRef.current) {
          vizRef.current.removeEventListener('tabswitch');
        }
        document.removeEventListener('fullscreenchange', setFullScreenHeight);
        document.removeEventListener(
          'mozfullscreenchange',
          setFullScreenHeight
        );
        document.removeEventListener('MSFullscreenChange', setFullScreenHeight);
        document.removeEventListener(
          'webkitfullscreenchange',
          setFullScreenHeight
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vizRef.current, tableauContainerRef.current, dimensionsRef.current]
  );

  useEffect(() => {
    setEventListeners(true);
    return () => {
      setEventListeners(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizRef.current, tableauContainerRef.current, dimensionsRef.current]);

  const hasErrorOccurred = hasContentError || error;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (isApiError) {
    return (
      <div className="tableau-report-wrapper">
        <TableauErrorMessage
          title={
            item.status
              ? 'Tableau file failed to clone for client view'
              : 'Unable to load file'
          }
          isShared={item.status}
        />
      </div>
    );
  }

  return (
    <div className="tableau-report-wrapper">
      {loading && !hasErrorOccurred && !ClientMessage && (
        <div className="mini-loader-container-wrapper">
          <MiniLoader dataTestId="library-item-loading-indicator" />
        </div>
      )}
      {hasErrorOccurred && (
        <TableauErrorMessage
          title="Unable to load file"
          isShared={item.status}
        />
      )}
      {loading &&
        showLoadingMsg &&
        !hasErrorOccurred &&
        !ClientMessage &&
        t('loadingTableauReport')}
      {!hasErrorOccurred && (
        <>
          <div data-testid="tableau-wrapper" className="tableau-wrapper">
            {ClientMessage || (
              <div
                className="tableau-container"
                ref={tableauContainerRef}
                data-testid="tableau-container"
              />
            )}
          </div>
          <div className="tableau-details">
            {autoSyncUrlRef.current &&
              item &&
              item.name &&
              !item.isShared &&
              !ClientMessage &&
              !isThirdPartyMember &&
              !loading && (
                <SyncedInfo
                  isPwcUser={isPwcUser}
                  projectId={projectId}
                  isTableau
                  tableauDisplayName={item.name}
                  tableauEmbedUrl={autoSyncUrlRef.current}
                />
              )}
            {!loading && (
              <div className="tableau-fullScreen-wrapper">
                <Button
                  className="button-link fullScreen-button"
                  onClick={() => openFullScreen(tableauContainerRef.current)}
                >
                  <Icon
                    name="full-screen"
                    width={22}
                    height={22}
                    className="outline icon-orange-theme"
                  />
                  <span>View fullscreen</span>
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TableauReport;
